import type {
  ColumnFiltersState,
  PaginationState,
  RowData,
  SortingState,
  TableOptions,
  TableState,
} from '@tanstack/react-table';
import {
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { StateCleanup } from './components/StateCleanup';
import type { BaseDataProviderProps } from './DataProvider';
import { Context } from './DataProviderContext';
import { DefaultColumnFilter } from './filter/DefaultColumnFilter';
import { FilterProvider } from './filter/FilterContext';
import { useTableState } from './hooks/useTableState';
import type { TableColumnDef } from './internal/getTableColumnDef';
import { useExtendedColumnDefs } from './useExtendedColumnDefs';

export interface DataProviderServerSideProps<
  TData extends RowData,
  TTexts = never,
> extends BaseDataProviderProps<TData, TTexts> {
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: (filter: ColumnFiltersState) => void;
  enableSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  pagination: PaginationState;
  onPaginationChange: (paging: PaginationState) => void;
  rowCount: number;
}

export const DataProviderServerSide = <TData extends RowData, TTexts = never>({
  children,
  columnDefs,
  initialData,
  initialState,
  actions = [],
  isLoading = false,
  isRowClickable,
  isRowHighlighted,
  onRowClick,
  toolbarControls,
  id,

  onColumnFiltersChange,
  columnFilters,
  enableSorting = false,
  sorting,
  onSortingChange,
  pagination,
  onPaginationChange,
  rowCount,
}: DataProviderServerSideProps<TData, TTexts>) => {
  const extendedColumnDefs = useExtendedColumnDefs<TData, TTexts>(columnDefs);
  const { tableState, onStateChange } = useTableState(id);
  const state: Partial<TableState> = {
    ...tableState,
  };
  const defaultColumn: Partial<TableColumnDef<TData, TTexts>> = {
    Filter: DefaultColumnFilter,
  };

  const options: TableOptions<TData> = {
    columns: extendedColumnDefs,
    data: initialData,
    state,
    initialState: initialState,
    getCoreRowModel: getCoreRowModel(),
    enableSorting: false,
    enableFilters: false,
    enableGrouping: false,
    enableRowSelection: false,
    defaultColumn,
    onStateChange,
  };

  if (enableSorting) {
    if (!sorting || !onSortingChange) {
      throw new Error('sorting and onSortingChange are required');
    }
    options.enableSorting = true;
    options.manualSorting = true;
    options.onSortingChange = (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      onSortingChange(next);
    };
    state.sorting = sorting;
  }

  // Column filter
  options.enableFilters = true;
  options.manualFiltering = true;
  options.onColumnFiltersChange = (updater) => {
    const next =
      typeof updater === 'function' ? updater(columnFilters) : updater;
    onColumnFiltersChange(next);
    onPaginationChange({ ...pagination, pageIndex: 0 });
  };
  state.columnFilters = columnFilters;

  // Pagination
  options.manualPagination = true;
  options.getPaginationRowModel = getPaginationRowModel();
  options.onPaginationChange = (updater) => {
    const next = typeof updater === 'function' ? updater(pagination) : updater;
    onPaginationChange(next);
  };
  options.pageCount = Math.ceil(rowCount / pagination.pageSize);
  state.pagination = pagination;

  const table = useReactTable(options);

  return (
    <Context.Provider
      value={{
        table,
        actions,
        toolbarControls,
        isLoading,
        isRowClickable,
        isRowHighlighted,
        onRowClick,
        getRowCount: () => rowCount,
      }}
    >
      <FilterProvider>
        <StateCleanup />
        {children}
      </FilterProvider>
    </Context.Provider>
  );
};
